var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ADD_ORDER from 'api/mutations/ADD_ORDER';
import REMOVE_ORDER from 'api/mutations/REMOVE_ORDER';
import GET_ORDERS from 'api/queries/GET_ORDERS';
import GET_ORDERS_STATS from 'api/queries/GET_ORDERS_STATS';
import { ORDERS_PAGE_SIZE } from 'constants/pagination';
import client from '../api/environment';
const initialState = {
    items: [],
    filters: {
        search: '',
        orderBy: '-modifiedDate',
        statuses: [],
        seasonId: null,
    },
    pageInfo: {
        hasNextPage: false,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        stats: 'idle',
        add: 'idle',
        remove: 'idle',
        other: 'idle',
    },
    errors: {
        fetch: null,
        stats: null,
        add: null,
        remove: null,
        other: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        remove: false,
        other: false,
    },
    cursors: [''], // Start with empty cursor for first page
    currentPage: 1,
    totalCount: null,
    delayedOrderIds: [],
    plannedOrderIds: [],
};
export const fetchOrders = createAsyncThunk('orders/fetchOrders', (arg_1, _a) => __awaiter(void 0, [arg_1, _a], void 0, function* (arg, { getState, rejectWithValue }) {
    const afterCursor = (arg === null || arg === void 0 ? void 0 : arg.afterCursor) || '';
    const state = getState();
    const { filters } = state.orders;
    try {
        const response = yield client.query({
            query: GET_ORDERS,
            variables: {
                orderBy: filters.orderBy || null,
                statuses: filters.statuses.length > 0 ? filters.statuses : null,
                seasonId: filters.seasonId || null,
                search: filters.search || null,
                first: ORDERS_PAGE_SIZE,
                after: afterCursor,
            },
            fetchPolicy: 'network-only',
            // without this fetchPolicy - no queries are send after the first one
        });
        return {
            orders: response.data.me.orders.edges.map((node) => node.node),
            pageInfo: response.data.me.orders.pageInfo,
            afterCursor,
            totalCount: response.data.me.ordersCount,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchOrdersStats = createAsyncThunk('orders/fetchOrdersStats', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    var _b, _c;
    const state = getState();
    const { filters } = state.orders;
    try {
        const response = yield client.query({
            query: GET_ORDERS_STATS,
            variables: {
                statuses: filters.statuses.length > 0 ? filters.statuses : null,
                seasonId: filters.seasonId || null,
            },
            fetchPolicy: 'network-only',
        });
        const stats = response.data.me.ordersStats;
        return {
            delayedOrderIds: (_b = stats === null || stats === void 0 ? void 0 : stats.delayedOrdersIds) !== null && _b !== void 0 ? _b : [],
            plannedOrderIds: (_c = stats === null || stats === void 0 ? void 0 : stats.plannedOrdersIds) !== null && _c !== void 0 ? _c : [],
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const goToPage = createAsyncThunk('orders/goToPage', (pageNumber_1, _a) => __awaiter(void 0, [pageNumber_1, _a], void 0, function* (pageNumber, { dispatch, rejectWithValue }) {
    if (pageNumber < 1) {
        return rejectWithValue('Invalid page number');
    }
    const cursor = pageNumber === 1 ? '' : `offset:${(pageNumber - 1) * ORDERS_PAGE_SIZE}`;
    return dispatch(fetchOrders({ afterCursor: cursor }));
}));
export const goToNextPage = createAsyncThunk('orders/goToNextPage', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, dispatch, rejectWithValue }) {
    const state = getState();
    const { pageInfo } = state.orders;
    if (!pageInfo.hasNextPage || !pageInfo.endCursor) {
        return rejectWithValue('No more pages');
    }
    return dispatch(fetchOrders({ afterCursor: pageInfo.endCursor }));
}));
export const goToPreviousPage = createAsyncThunk('orders/goToPreviousPage', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, dispatch, rejectWithValue }) {
    const state = getState();
    const { currentPage } = state.orders;
    if (currentPage <= 1) {
        return rejectWithValue('Already on first page');
    }
    return dispatch(goToPage(currentPage - 1));
}));
export const addOrder = createAsyncThunk('orders/addOrder', () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const orderData = yield client.mutate({
            mutation: ADD_ORDER,
            fetchPolicy: 'no-cache',
        });
        return orderData.data.addOrder.order;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeOrder = createAsyncThunk('orders/removeOrder', (orderId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_ORDER,
            variables: { orderNodeId: orderId },
        });
        return orderId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        resetOrdersState(state) {
            state.items = [];
            state.status.fetch = 'idle';
            state.status.stats = 'idle';
            state.errors.fetch = null;
            state.errors.stats = null;
            state.refetchFlags.fetch = false;
            state.delayedOrderIds = [];
            state.plannedOrderIds = [];
        },
        setOrdersRefetchFlag(state, action) {
            state.refetchFlags.fetch = action.payload;
        },
        setFilterSearch(state, action) {
            const { value } = action.payload;
            state.filters.search = value;
        },
        setFilterOrder(state, action) {
            const { value } = action.payload;
            state.filters.orderBy = value;
        },
        setFilterSeason(state, action) {
            const { value } = action.payload;
            state.filters.seasonId = value;
        },
        setFilters(state, action) {
            state.filters = action.payload;
        },
        setFilterStatuses(state, action) {
            const { value } = action.payload;
            if (state.filters.statuses.includes(value)) {
                state.filters.statuses = state.filters.statuses.filter((status) => status !== value);
            }
            else {
                state.filters.statuses = [...state.filters.statuses, value];
            }
        },
        resetFilters(state) {
            state.filters = {
                search: '',
                orderBy: '-modifiedDate',
                statuses: [],
                seasonId: null,
            };
            state.cursors = [''];
            state.currentPage = 1;
        },
        resetPagination(state) {
            state.cursors = [''];
            state.currentPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrders.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchOrders.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.items = action.payload.orders;
            state.pageInfo = action.payload.pageInfo;
            state.refetchFlags.fetch = false;
            if (action.payload.totalCount !== undefined && action.payload.totalCount !== null) {
                state.totalCount = action.payload.totalCount;
            }
            const { afterCursor, pageInfo } = action.payload;
            if (afterCursor === '') {
                state.currentPage = 1;
                if (state.cursors.length === 0 || state.cursors[0] !== '') {
                    state.cursors = [''];
                }
            }
            else if (afterCursor.startsWith('offset:')) {
                const offset = parseInt(afterCursor.substring(7), 10);
                state.currentPage = Math.floor(offset / ORDERS_PAGE_SIZE) + 1;
            }
            else {
                const cursorIndex = state.cursors.indexOf(afterCursor);
                if (cursorIndex !== -1) {
                    state.currentPage = cursorIndex + 1;
                }
                else {
                    state.cursors.push(afterCursor);
                    state.currentPage = state.cursors.length;
                }
            }
            if (pageInfo.hasNextPage && pageInfo.endCursor) {
                const nextCursorIndex = state.currentPage;
                while (state.cursors.length <= nextCursorIndex) {
                    state.cursors.push('');
                }
                state.cursors[nextCursorIndex] = pageInfo.endCursor;
            }
        })
            .addCase(fetchOrders.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.payload || 'Failed to fetch orders';
        })
            .addCase(fetchOrdersStats.pending, (state) => {
            state.status.stats = 'loading';
            state.errors.stats = null;
            state.delayedOrderIds = [];
            state.plannedOrderIds = [];
        })
            .addCase(fetchOrdersStats.fulfilled, (state, action) => {
            var _a, _b;
            state.status.stats = 'succeeded';
            state.delayedOrderIds = (_a = action.payload.delayedOrderIds) !== null && _a !== void 0 ? _a : [];
            state.plannedOrderIds = (_b = action.payload.plannedOrderIds) !== null && _b !== void 0 ? _b : [];
        })
            .addCase(fetchOrdersStats.rejected, (state, action) => {
            state.status.stats = 'failed';
            state.errors.stats = action.payload || 'Failed to fetch orders stats';
            state.delayedOrderIds = [];
            state.plannedOrderIds = [];
        })
            .addCase(addOrder.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addOrder.fulfilled, (state) => {
            state.status.add = 'succeeded';
        })
            .addCase(addOrder.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add order';
        })
            .addCase(removeOrder.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeOrder.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeOrder.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { resetOrdersState, setOrdersRefetchFlag, setFilterSearch, setFilterOrder, setFilterSeason, setFilterStatuses, setFilters, resetFilters, resetPagination, } = ordersSlice.actions;
export const selectOrdersStatus = (state) => state.orders.status;
export const selectOrdersStatsStatus = (state) => { var _a; return (_a = state.orders.status) === null || _a === void 0 ? void 0 : _a.stats; };
export const selectOrdersErrors = (state) => state.orders.errors;
export const selectOrdersRefetchFlags = (state) => state.orders.refetchFlags;
export const selectOrdersFilters = (state) => state.orders.filters;
export const selectOrders = (state) => state.orders.items;
export const selectOrdersPageInfo = (state) => state.orders.pageInfo;
export const selectCurrentPage = (state) => state.orders.currentPage;
export const selectOrdersTotalCount = (state) => state.orders.totalCount;
export const selectDelayedOrderIds = (state) => state.orders.delayedOrderIds;
export const selectPlannedOrderIds = (state) => state.orders.plannedOrderIds;
export const selectPageNumbers = (state) => {
    // Show all pages we have cursors for
    const { cursors } = state.orders;
    const pages = [];
    for (let i = 1; i <= cursors.length; i += 1) {
        pages.push(i);
    }
    return pages;
};
export default ordersSlice.reducer;
